import { SitecoreContextValue } from '@sitecore-jss/sitecore-jss-react';
import { env } from 'lib/env';
import { findComponentInRendering } from 'lib/sitecore/utils';

export type SearchSortContext = {
  defaultSortByValue?: string;
  defaultSortBySuffix?: string;
  useTcDirectoryRouteState: boolean;
};

type SearchSortContextCarrier = {
  searchSortContext?: Partial<SearchSortContext>;
};

const getMainIndex = (): string => env.ALGOLIA_INDEX_NAME.toLowerCase();

const getReportsIndex = (): string | undefined => env.ALGOLIA_REPORTS_INDEX_NAME?.toLowerCase();

const hasComponentInRoute = (
  sitecoreContext: SitecoreContextValue,
  componentName: string
): boolean => {
  const placeholders = sitecoreContext.route?.placeholders;

  if (!placeholders) {
    return false;
  }

  return Object.values(placeholders).some((renderings) => {
    if (!Array.isArray(renderings)) {
      return false;
    }

    return renderings.some((rendering) => !!findComponentInRendering(rendering, componentName));
  });
};

const normalizeConfiguredSortValue = (sortValue: string | undefined): string | undefined => {
  const mainIndex = getMainIndex();
  const reportsIndex = getReportsIndex();
  const normalizedSortValue = sortValue?.trim().toLowerCase();

  if (!normalizedSortValue) {
    return undefined;
  }

  if (normalizedSortValue === mainIndex || normalizedSortValue.startsWith(`${mainIndex}_`)) {
    return normalizedSortValue;
  }

  if (
    reportsIndex &&
    (normalizedSortValue === reportsIndex || normalizedSortValue.startsWith(`${reportsIndex}_`))
  ) {
    return normalizedSortValue;
  }

  return `${mainIndex}_${normalizedSortValue}`;
};

const getPrecomputedSearchSortContext = (
  sitecoreContext: SitecoreContextValue
): Partial<SearchSortContext> | undefined => {
  const carrier = sitecoreContext as SitecoreContextValue & SearchSortContextCarrier;
  return carrier.searchSortContext;
};

const getDefaultSortByValueForComponent = (
  sitecoreContext: SitecoreContextValue
): string | undefined => {
  const mainIndex = getMainIndex();
  const reportsIndex = getReportsIndex();

  if (hasComponentInRoute(sitecoreContext, 'EmployeeBlogListing')) {
    const sortIndexValue = sitecoreContext.route?.fields?.sortIndex?.value as string | undefined;
    return normalizeConfiguredSortValue(sortIndexValue);
  }

  if (hasComponentInRoute(sitecoreContext, 'SearchListing')) {
    return `${mainIndex}_date_desc`;
  }

  if (
    hasComponentInRoute(sitecoreContext, 'Reports') ||
    hasComponentInRoute(sitecoreContext, 'CTInstructions') ||
    hasComponentInRoute(sitecoreContext, 'CourierReports') ||
    hasComponentInRoute(sitecoreContext, 'FeeSchedule')
  ) {
    return reportsIndex ? `${reportsIndex}_date_desc` : `${mainIndex}_date_desc`;
  }

  if (
    hasComponentInRoute(sitecoreContext, 'AnnouncementListing') ||
    hasComponentInRoute(sitecoreContext, 'RecentNetworkAnnouncements')
  ) {
    return `${mainIndex}_date_desc`;
  }

  if (hasComponentInRoute(sitecoreContext, 'EventListing')) {
    return `${mainIndex}_date_asc`;
  }

  return undefined;
};

export const getSearchSortContext = (sitecoreContext: unknown): SearchSortContext => {
  const typedContext = sitecoreContext as SitecoreContextValue;
  const mainIndex = getMainIndex();
  const precomputedContext = getPrecomputedSearchSortContext(typedContext);
  const defaultSortByValue =
    precomputedContext?.defaultSortByValue ?? getDefaultSortByValueForComponent(typedContext);

  return {
    defaultSortByValue,
    defaultSortBySuffix:
      precomputedContext?.defaultSortBySuffix ??
      (defaultSortByValue?.startsWith(`${mainIndex}_`)
        ? defaultSortByValue.substring(mainIndex.length + 1)
        : undefined),
    useTcDirectoryRouteState:
      precomputedContext?.useTcDirectoryRouteState ??
      hasComponentInRoute(typedContext, 'TcDirectorySearch'),
  };
};

/**
 * Computes the default sort suffix to use when URL state has no sortBy.
 */
export const getDefaultSortBySuffix = (sitecoreContext: unknown): string | undefined => {
  return getSearchSortContext(sitecoreContext).defaultSortBySuffix;
};

/**
 * Computes the default full sort value to use when URL state has no sortBy.
 */
export const getDefaultSortByValue = (sitecoreContext: unknown): string | undefined => {
  return getSearchSortContext(sitecoreContext).defaultSortByValue;
};

/**
 * Determines whether current page contains a TC Directory Search component.
 */
export const isTcDirectorySearchPage = (sitecoreContext: unknown): boolean => {
  return getSearchSortContext(sitecoreContext).useTcDirectoryRouteState;
};
